// Copyright (C) 2022 TANNER AG

import React from "react";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import clsx from "clsx";
import {To} from "react-router-dom";
import ListItemRegularAction from "../../components/listitem/regular/ListItemRegularAction";
import ListItemRegularImage from "../../components/listitem/regular/ListItemRegularImage";
import {CheckIcon} from "../../components/icons/MANIcons";
import {Chip} from "@mui/material";
import PreviewTag from "../../components/PreviewTag";

type Props = {
    title?: string;
    description?: string;
    image?: string;
    series?: string[];
    action?: React.ReactNode;
    toolbar?: React.ReactNode;
    additional?: React.ReactNode;
    border?: boolean;
    selected?: boolean;
    inactive?: boolean;
    children?: React.ReactNode;
    no?: string | number;
    to?: To;
    href?: string;
    onClick?(): void;
    bustTypes?: string[];
    isPreview?: boolean | null;
};

const CodeListItemRegular: React.FC<Props> = ({
                                                  title,
                                                  description,
                                                  image,
                                                  action,
                                                  toolbar,
                                                  border,
                                                  selected,
                                                  inactive,
                                                  additional,
                                                  no,
                                                  to,
                                                  href,
                                                  onClick,
                                                  series,
                                                  bustTypes,
                                                  isPreview
                                              }) => {

            const chipItems = (series && series.length) ? series : bustTypes;

            const ChipsList: React.FC<{items?: string[]}> = ({items}) => {
                if (!items || !items.length) return null;
                return (
                    <Stack
                        direction="row"
                        sx={{
                            mt: 1,
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        {items.map((s) => (
                            <Chip
                                key={s}
                                label={s}
                                size="small"
                                variant="outlined"
                                sx={{color: "text.secondary", borderColor: "grey.300"}}
                            />
                        ))}
                    </Stack>
                );
            };

    return (
        <CodeListItemWrapper border={border} selected={selected}>
            <Stack sx={{p: 1}}>
                <ListItemRegularAction onClick={onClick} href={href} to={to}>
                    <ListItemRegularImage title={title} src={image} inactive={inactive}/>
                </ListItemRegularAction>
            </Stack>
            <Stack sx={{p: 1, flexGrow: 1}}>
                <>
                    <Stack sx={{mr: 6}}>
                        <Stack direction="column">
                            <PreviewTag isPreview={isPreview} sx={{ mb: 1, alignSelf: "flex-start" }} />
                            <ListItemRegularAction onClick={onClick} href={href} to={to}>
                                <Typography
                                    variant="h4"
                                    sx={{lineHeight: "20px", fontSize: 18, color: "inherit"}}
                                    className="heading"
                                    gutterBottom
                                >
                                    {no}
                                </Typography>
                            </ListItemRegularAction>
                            {title && (
                                <Typography
                                    variant="h5"
                                    sx={{fontSize: 16, color: "text.secondary", minWidth: 40}}
                                    className="heading"
                                    gutterBottom
                                >
                                    {title}
                                </Typography>
                            )}
                        </Stack>

                        <Typography
                            variant="body2"
                            sx={{pr: 2}}
                            dangerouslySetInnerHTML={{__html: description ?? ""}}
                            color="textSecondary"
                            component="div"
                            gutterBottom
                        />

                    </Stack>
                    <Stack direction="row" sx={{justifyContent: "space-between", alignItems: "center", mt: 0.5}}>
                        <div>{additional}</div>
                        <div>{action}</div>
                    </Stack>
                    <ChipsList items={chipItems} />

                </>
            </Stack>
            {
                selected && (
                    <Fab
                        color="secondary"
                        size="small"
                        sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: {xs: 28, sm: 35},
                            height: {xs: 28, sm: 35},
                            minHeight: "auto",
                            userSelect: "none",
                            pointerEvents: "none"
                        }}
                    >
                        <CheckIcon fontSize="small"/>
                    </Fab>
                )
            }
            {
                Boolean(toolbar) &&
                <Stack direction="row" spacing={1} sx={{position: "absolute", top: {xs: 4, sm: 12}, right: 12}}>
                    {toolbar}
                </Stack>
            }
        </CodeListItemWrapper>
    )
        ;
};

export const CodeListItemWrapper: React.FC<Pick<Props, "border" | "selected" | "children">> = ({
                                                                                                   border,
                                                                                                   selected,
                                                                                                   children
                                                                                               }) => {
    return (
        <Stack
            component={Paper}
            direction="row"
            className={clsx({border, selected})}
            sx={{
                position: "relative",
                transition: "all .3s",
                "&.border": {border: "1px solid", borderColor: "grey.100"},
                "&.selected": {border: "1px solid", borderColor: "secondary.main"}
            }}
        >
            {children}
        </Stack>
    );
};

export default CodeListItemRegular;
